import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTable } from "@refinedev/antd";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Tag,
  Button,
  Badge,
  Card,
  message,
} from "antd";
import {
  CrudFilters,
  HttpError,
  useGetIdentity,
  useNavigation,
} from "@refinedev/core";
import {
  ApiOutlined,
  CheckOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SettingOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { PaginationTotal } from "@/components/pagination-total";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { User } from "@/types/schema/schema.types";
import { useNavigate } from "react-router-dom";
import {
  API_URL_PERMISSIONS_STORES,
  API_URL_STORE_LINKED_SELLER,
} from "@/services/XCONS_ENV";
import { KeyRound } from "@/components/icon/Key";
import { useSmartMenuLink } from "@/config/context/smartMenuNavigation";
import {
  FilterTags,
  LateralModal,
  NormalModal,
  SellerIcon,
} from "@/components";
import { LinkedSellersFilters } from "./linkedSellersFilters";
import { ExternalLink } from "@/components/icon/ExternalLink";
import { axiosInstance } from "@/utilities";
import dayjs from "dayjs";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text, Link } = Typography;
const { Search } = Input;

export const LinkedSellerList: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  const { data: dataLoguinUser } = useGetIdentity<User>();
  const { goToSmartMenu } = useSmartMenuLink();
  const { smartMenuName } = useContext(SmartMenuContext);
  const [isRequestLoading, setIsRequestLoading] = useState<number | null>(null);
  const [typeIdentifier, setTypeIdentifier] = useState("store");
  const [localData, setLocalData] = useState<any[]>([]);

  const storeCode = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );

  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const {
      main_search,
      sellerStatus,
      linkStatus,
      requestStatus,
      filters: existingFilters,
    } = params;

    if (existingFilters) {
      existingFilters.forEach((filter: any) => {
        filters.push(filter);
      });
    }

    filters.push({
      field: "search",
      operator: "contains",
      value: main_search,
    });

    // Seller status filter maps to two backend fields
    if (sellerStatus) {
      const statusMap: Record<string, { linkActive: number; sellerActive: number }> = {
        active: { linkActive: 1, sellerActive: 1 },
        paused_by_seller: { linkActive: 1, sellerActive: 0 },
        paused_by_store: { linkActive: 0, sellerActive: 1 },
        paused_by_store_and_seller: { linkActive: 0, sellerActive: 0 },
      };
      const mapped = statusMap[sellerStatus];
      if (mapped) {
        filters.push(
          { field: "storeLinkedSellers.active", operator: "eq", value: mapped.linkActive },
          { field: "isActive", operator: "eq", value: mapped.sellerActive },
        );
      }
    }

    if (linkStatus !== undefined && linkStatus !== null) {
      filters.push({
        field: "storeLinkedSellers.active",
        operator: "eq",
        value: linkStatus,
      });
    }

    if (requestStatus) {
      filters.push({
        field: "storeLinkedSellerRequests.requestBy",
        operator: "eq",
        value: requestStatus,
      });
    }

    setFilters(filters);
    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching, refetch },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: `store-linked-seller/${storeCode}/sellers`,
    pagination: {
      pageSize: FILAS_POR_PAGINA,
    },
    onSearch: onSearch,
  });

  const isLoadingTable = !data || isFetching;

  const total = data?.total || 0;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : localData || []),
    [isLoadingTable, localData],
  );

  useEffect(() => {
    if (data?.data) {
      setLocalData(data.data);
    }
  }, [data?.data]);

  const updateLocalSeller = (sellerId: string, updates: Partial<any>) => {
    setLocalData((prev) =>
      prev.map((seller) =>
        seller.sellerId === sellerId ? { ...seller, ...updates } : seller,
      ),
    );
  };

  const handleLinkRequest = async (record: any) => {
    setIsRequestLoading(record.sellerId);

    const tempId = `temp-${Date.now()}`;
    updateLocalSeller(record.sellerId, {
      storeLinkedSellerRequests: {
        id: tempId,
        storeCode: storeCode,
        sellerCode: record.sellerCode,
        createdAt: new Date().toISOString(),
        requestBy: typeIdentifier,
        active: true,
      },
    });

    if (
      record.storeLinkedSellerRequests &&
      record.storeLinkedSellerRequests.requestBy === "seller"
    ) {
      updateLocalSeller(record.sellerId, {
        storeLinkedSellers: {
          storeSellerId: record.storeLinkedSellerRequests.id,
          active: true,
          createdAt: new Date().toISOString(),
        },
      });
    }

    try {
      const payload = {
        storeCode: storeCode,
        sellerCode: record.sellerCode,
        requestBy: typeIdentifier,
        active: true,
      };

      const response = await axiosInstance.post(
        `${API_URL_STORE_LINKED_SELLER}/request`,
        payload,
      );

      // The endpoint may return EITHER a request (StoreLinkedSellerRequest)
      // OR a link (StoreLinkedSeller, when the opposite party already had a
      // pending request and the backend auto-resolves it). Discriminate by
      // presence of `storeSellerId` — only the Link entity has it. We can't
      // use absence-of-requestBy because TypeORM `save(dto)` mutates and
      // returns the input object, so the Link response carries a "ghost"
      // `requestBy` copied from the DTO body.
      const data = response.data;
      const isLink =
        !!data &&
        typeof data === "object" &&
        ("storeSellerId" in data || "store_seller_id" in data);

      if (isLink) {
        updateLocalSeller(record.sellerId, {
          storeLinkedSellers: data,
          // Linked supersedes the pending request from either side.
          storeLinkedSellerRequests: null,
        });
      } else {
        updateLocalSeller(record.sellerId, {
          storeLinkedSellerRequests: data,
        });
      }

      message.success(t("request.sent.successfully"));
    } catch (error: any) {
      updateLocalSeller(record.sellerId, {
        storeLinkedSellerRequests: null,
      });

      console.error("Error sending request:", error?.message);
      message.error(error?.message || "Failed to send request");
    } finally {
      setIsRequestLoading(null);
    }
  };

  const handleCancelRequest = async (record: any) => {
    setIsRequestLoading(record.sellerId);

    const previousState = localData.find((s) => s.sellerId === record.sellerId);

    updateLocalSeller(record.sellerId, {
      storeLinkedSellerRequests: null,
      storeLinkedSellers: null,
    });

    try {
      await axiosInstance.delete(
        `${API_URL_STORE_LINKED_SELLER}/request/${record?.storeLinkedSellerRequests?.id}`,
      );

      message.success(t("request.cancelled.successfully"));
    } catch (error: any) {
      if (previousState) {
        updateLocalSeller(record.sellerId, {
          storeLinkedSellerRequests: previousState.storeLinkedSellerRequests,
          storeLinkedSellers: previousState.storeLinkedSellers,
        });
      }

      console.error("Error cancelling request:", error?.message);
      message.error(error?.message || "Failed to cancel request");
    } finally {
      setIsRequestLoading(null);
    }
  };

  const columns: TableColumnsType<any> = [
    {
      key: "name",
      title: "Sellers",
      sorter: (a, b) => a.name.localeCompare(b.name),
      dataIndex: "name",
      render: (_, record) => {
        return (
          <Flex align="center" gap={4}>
            <SellerIcon />
            <Text>{record.name}</Text>
            <Link
              href={`https://www.xcons.com.ar/storelocation/${record.sellerCode}`}
              target="_blank"
            >
              <ExternalLink />
            </Link>
          </Flex>
        );
      },
    },
    {
      key: "sellerCode",
      title: t("posts.fields.status.title"),
      dataIndex: "sellerCode",
      render: (_, record) => {
        const linkedSeller = Array.isArray(record.storeLinkedSellers)
          ? record.storeLinkedSellers[0]
          : record.storeLinkedSellers;
        const hasLink = !!linkedSeller;

        return (
          <Flex justify="start" align="start" gap={4} vertical>
            {hasLink && (
              <>
                <Flex gap={4}>
                  <Badge
                    status={linkedSeller?.active ? "success" : "warning"}
                  />
                  <Text>
                    Store tiene el Seller{" "}
                    {linkedSeller?.active ? t("filter.products.labelStatusActive") : t("filter.products.labelStatusPaused")}
                  </Text>
                </Flex>
                <Flex gap={4}>
                  <Badge
                    status={record?.isActive ? "success" : "warning"}
                  />
                  <Text>
                    Seller tiene ventas{" "}
                    {record?.isActive
                      ? t("filter.products.labelStatusActive")
                      : t("filter.products.labelStatusPaused")}
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        );
      },
      width: "50%",
    },
    {
      key: "sellerId",
      title: "Solicitud",
      dataIndex: "sellerId",
      render: (_, record) => {
        const linkedSeller = Array.isArray(record.storeLinkedSellers)
          ? record.storeLinkedSellers[0]
          : record.storeLinkedSellers;

        const request = Array.isArray(record.storeLinkedSellerRequests)
          ? record.storeLinkedSellerRequests[0]
          : record.storeLinkedSellerRequests;

        const hasLink = !!linkedSeller;
        const hasRequest = !!request;

        const shouldShowLinkRequest = !hasLink && !hasRequest;
        const isSellerRequest = request?.requestBy === "seller";
        const isStoreRequest = request?.requestBy === "store";

        return (
          <Flex vertical justify="end" align="end">
            {shouldShowLinkRequest && (
              <>
                <Button
                  loading={isRequestLoading === record.sellerId}
                  size="small"
                  icon={<ApiOutlined />}
                  iconPosition="end"
                  onClick={() => handleLinkRequest(record)}
                >
                  {t("button.table.linkedsellers.linkrequest")}
                </Button>
              </>
            )}

            {isSellerRequest && !hasLink && (
              <Flex gap={8} vertical align="end">
                <Text style={{ color: token.colorTextSecondary }}>
                  {t("table.linkedsellers.description.requestsenton")}{" "}
                  {dayjs(request?.createdAt).format("DD/MM/YYYY")}
                </Text>
                <Flex align="center" gap={8}>
                  <Button
                    loading={isRequestLoading === record.sellerId}
                    type="link"
                    danger
                    size="small"
                    onClick={() => handleCancelRequest(record)}
                  >
                    {t("button.table.linkedsellers.reject")}
                  </Button>
                  <Button
                    loading={isRequestLoading === record.sellerId}
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />} iconPosition="end"
                    onClick={() => handleLinkRequest(record)}
                  >
                    Accept
                  </Button>
                </Flex>
              </Flex>
            )}

            {isStoreRequest && !hasLink && (
              <Flex gap={8} vertical align="end">
                <Text style={{ color: token.colorTextSecondary }}>
                  {t("table.linkedsellers.description.requestsenton")}{" "}
                  {dayjs(request?.createdAt).format("DD/MM/YYYY")}
                </Text>
                <Button
                  loading={isRequestLoading === record.sellerId}
                  type="link"
                  danger
                  size="small"
                  onClick={() => handleCancelRequest(record)}
                >
                  {t("orders.tag.cancelrequest")}
                </Button>
              </Flex>
            )}

            {hasLink && (
              <Flex gap={8} vertical align="end">
                <Text style={{ color: token.colorTextSecondary }}>
                  {t("table.linkedsellers.description.linkedselleron")}{" "}
                  {dayjs(linkedSeller?.createdAt).format("DD/MM/YYYY")}
                </Text>
                <Button
                  loading={isRequestLoading === record.sellerId}
                  size="small"
                  icon={<SettingOutlined />}
                  iconPosition="end"
                  onClick={() => handleRowClick(record)}
                >
                  {t("button.table.linkedsellers.configure")}
                </Button>
              </Flex>
            )}
          </Flex>
        );
      },
    },
  ];

  const HeaderRow = React.memo(() => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="main_search">
            <Search
              placeholder={"Buscar por nombre"}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
          className="add-user-button"
        >
          <LateralModal
            disabled={isFetching}
            buttonName={t("buttons.filters")}
            title={t("filter.title.addFilter")}
          >
            <LinkedSellersFilters
              searchFormProps={searchFormProps}
              filters={filters}
              t={t}
            />
          </LateralModal>
        </Flex>
      </Flex>

      <FilterTags
        searchFormProps={searchFormProps}
        onSearch={onSearch}
        i18n={i18n}
        filters={filters}
        setFilters={setFilters}
        t={t}
      />
    </>
  ));

  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`/stores/linked-sellers/${storeCode}/${record?.sellerCode}`, {
      state: { record: record },
    });
  };

  return (
    <>
      <Title level={3} style={{ display: "flex", alignItems: "center" }}>
        <ShopOutlined style={{ marginRight: "10px" }} />
        <span style={{ textTransform: "capitalize", marginRight: "10px" }}>
          {storeCode}
        </span>
        {t("pages.linked sellers.title")}
      </Title>
      <Table
        {...tableProps}
        scroll={{ x: true }}
        title={() => <HeaderRow />}
        rowKey={(record) => record?.sellerId}
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        locale={{
          triggerAsc: t("table.triggerAsc"),
          triggerDesc: t("table.triggerDesc"),
          cancelSort: t("table.cancelSort"),
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          locale: {
            items_per_page: t("table.itemsPerPage"),
            prev_page: t("table.prevPage"),
            next_page: t("table.nextPage"),
            jump_to: t("table.jumpTo"),
            jump_to_confirm: t("table.jumpToConfirm"),
            page: t("table.page"),
            next_5: t("table.next5"),
            prev_5: t("table.prev5"),
          },
          showTotal: (total) => {
            return <PaginationTotal total={total} entityName={t("sellers")} />;
          },
        }}
      />
    </>
  );
};
